import React from 'react';
import styled from 'styled-components';
import Button from './Button';
import { FaBed, FaUsers, FaExpand } from 'react-icons/fa';
import Icon from './Icon';

interface RoomCardProps {
  image: string;
  title: string;
  description: string;
  beds: number;
  capacity: number;
  size: number;
  price?: number | null;
  link: string;
}

const RoomCard: React.FC<RoomCardProps> = ({
  image,
  title,
  description,
  beds,
  capacity,
  size,
  price,
  link,
}) => {
  return (
    <RoomCardContainer>
      <RoomImage src={image} alt={title} />
      <RoomContent>
        <RoomTitle>{title}</RoomTitle>
        <RoomDescription>{description}</RoomDescription>
        <RoomFeatures>
          <RoomFeature>
            <Icon component={FaBed} />
            <span>{beds} lit{beds > 1 ? 's' : ''}</span>
          </RoomFeature>
          <RoomFeature>
            <Icon component={FaUsers} />
            <span>{capacity} personne{capacity > 1 ? 's' : ''}</span>
          </RoomFeature>
          <RoomFeature>
            <Icon component={FaExpand} />
            <span>{size} m²</span>
          </RoomFeature>
        </RoomFeatures>
        {price && (
          <RoomPrice>
            <span>À partir de</span>
            <strong>{price}€</strong>
            <span>/ nuit</span>
          </RoomPrice>
        )}
        <RoomButton>
          <Button to={link} variant="outline">
            Voir les photos
          </Button>
        </RoomButton>
      </RoomContent>
    </RoomCardContainer>
  );
};

const RoomCardContainer = styled.div`
  background-color: var(--color-light);
  box-shadow: var(--shadow);
  transition: var(--transition);
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const RoomImage = styled.img`
  width: 100%;
  height: 25rem;
  object-fit: cover;
  transition: var(--transition);
  
  ${RoomCardContainer}:hover & {
    transform: scale(1.05);
  }
`;

const RoomContent = styled.div`
  padding: 3rem;
`;

const RoomTitle = styled.h3`
  font-size: 2.4rem;
  margin-bottom: 1.5rem;
`;

const RoomDescription = styled.p`
  font-size: 1.5rem;
  color: var(--color-secondary);
  margin-bottom: 2rem;
`;

const RoomFeatures = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const RoomFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.4rem;
  
  svg {
    color: var(--color-accent);
    font-size: 1.8rem;
  }
`;

const RoomPrice = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 2rem;
  
  span {
    font-size: 1.4rem;
    color: var(--color-secondary);
  }
  
  strong {
    font-size: 2.4rem;
    font-weight: 700;
    color: var(--color-accent);
  }
`;

const RoomButton = styled.div`
  display: flex;
  justify-content: center;
`;

export default RoomCard;
